import React, {useEffect, useState} from 'react';
import MainLayout from '../../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {Theme} from '../../../../theme/lightTheme';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {getHomeTabItemById} from '../../../../utils/helpers';
import {RootState} from '../../../../redux/store';
import {useSelector} from 'react-redux';
import userVoice from '../../../../utils/services/userVoice';
import {FilterType} from '../../../../interfaces/reducers/homeState';
import {Space} from '../../home';
import {LinearGradient} from 'react-native-linear-gradient';
import {useCustomHeaderHeight} from '../../../../utils/hooks/useHeaderHeight';
import ProgressiveImage from '../../../../components/progressiveImage';
import {
  homePopularTopicsTabs,
  tabImages,
} from '../../../../assets/static/homeTabs';
import Icon from '../../../../components/icon';
import {tr} from '../../../../translations';
import TextGeneric from '../../../../components/textGeneric';
import FiltersModalSkeleton from '../../../../components/filtersModalSkeleton';
import dayjs, {Dayjs} from 'dayjs';
import {Platform, StyleSheet, TouchableOpacity, View} from 'react-native';
import {openDateRangePickerModal} from '../../../../connected-components/dateRangePickerModal/actions';
import {handleChange} from '../../../../utils/form';
import TextInput from '../../../../components/textInput';
import {toggleSettingsModal} from '../../../../connected-components/settingsModal/actions';
import FiltersSheet from '../../../../components/filtersSheet';
import GenericButton from '../../../../components/button';
import useResponsiveDimensions from '../../../../utils/hooks/useResponsiveDimensions';
import {FilterTabNewsProps} from '../../../../interfaces/screens/filterTabNews';
import screens from '../../../../config/screens';
import {NavigationProp, useNavigation} from '@react-navigation/native';

const isWeb = Platform.OS === 'web';

const Container = styled.View`
  width: 100%;
  height: 100%;
`;

const ContentWrapper = styled.View<{maxWidth: number}>`
  width: 100%;
  ${({maxWidth}) => isWeb && `max-width: ${maxWidth}px; margin: 0 auto;`}
`;

const TopContainer = styled.View<{width: string}>`
  padding-top: ${({theme}) => theme.text.s5}px;
  width: ${({width}) => width};
  align-items: center;
  justify-content: center;
  align-self: center;
`;

const IconContainer = styled.View`
  width: ${({theme}) => theme.text.s1 * 6}px;
  aspect-ratio: 1;
  z-index: 1;
`;

const ImageContainer = styled.View<{width: string}>`
  width: ${({theme}) => theme.text.s1 * 4.2}px;
  aspect-ratio: 1;
  position: absolute;
  z-index: 2;
`;

const Image = styled(ProgressiveImage)`
  width: 100%;
  aspect-ratio: 1;
`;

const TitleContainer = styled.View<{width: string}>`
  width: ${({width}) => width};
  align-self: center;
  padding-top: ${({theme}) => theme.text.s5}px;
`;

const Title = styled(TextGeneric)<{fontSize: number}>`
  color: ${({theme}) => theme.text.light};
  font-size: ${({fontSize}) => fontSize}px;
  line-height: ${({fontSize}) => fontSize * 1.1}px;
`;

const FiltersContainer = styled.View<{width: string}>`
  width: ${({width}) => width};
  align-self: center;
  padding-top: ${({theme}) => theme.text.s5}px;
`;

const TouchableContainer = styled.TouchableOpacity<{width: string}>`
  width: ${({width}) => width};
  align-self: center;
  align-items: center;
  justify-content: center;
  padding-top: ${({theme}) => theme.text.s5 / 2}px;
`;

const ResetFilter = styled(TextGeneric)<{
  isUnderline?: boolean;
  fontSize: number;
}>`
  color: ${({theme}) => theme.text.light};
  font-size: ${({fontSize}) => fontSize}px;
  text-align: center;
  ${({isUnderline}) => isUnderline && 'text-decoration-line: underline;'}
`;

const ButtonContainer = styled.View`
  width: 100%;
  padding-bottom: ${({theme}) => theme.text.s5 / 2}px;
`;

// Define styles for web shadows and gradient
const styles = StyleSheet.create({
  button: {
    ...(isWeb && {
      shadowColor: '#000',
      shadowOffset: {width: 0, height: 4},
      shadowOpacity: 0.1,
      shadowRadius: 10,
    }),
  },
  webPointer: {
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  gradientContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    zIndex: -1,
  },
  contentContainer: {
    flex: 1,
    width: '100%',
  },
});

/**
 * FilterTabNews screen component.
 *
 * @param {FilterTabNewsProps} props - The screen component props.
 */
const FilterTabNews: React.FC<FilterTabNewsProps> = ({
  route,
  isComponent,
  topComponent,
}: FilterTabNewsProps) => {
  const theme = useTheme() as Theme;
  const navigation = useNavigation<NavigationProp<any>>();
  const {bottom: bottomInset} = useSafeAreaInsets();
  const tabNews = route?.params || {};
  const {
    id: tab_id,
    tab_value,
    tab_name: tabName,
    filters: prevTabFilters,
  } = tabNews;
  const headerHeight = useCustomHeaderHeight();
  const {hdp, getByScreenSize, isMobile} = useResponsiveDimensions();

  const {currentUser} = useSelector((state: RootState) => state.auth);
  const {tabs} = useSelector((state: RootState) => state.homeTabs);
  const [filtersResult, setFiltersResult] = useState<any>({});
  const [tabFilters, setTabFilters] = useState<FilterType[]>(
    prevTabFilters || [],
  );

  const initTabInfo = () => {
    const homeTabItem = getHomeTabItemById(tab_id);
    setTabFilters(homeTabItem?.filters || []);
  };

  useEffect(() => {
    initTabInfo();
  }, [tabs]);

  useEffect(() => {
    setFiltersResult({});
  }, [currentUser?.specialty_id]);

  useEffect(() => {
    return () => {
      userVoice?.stop();
    };
  }, []);

  const getValue = (filterItem: FilterType) => {
    const id = filterItem?.key ? filtersResult[filterItem?.key] : -1;
    const idx = (filterItem?.items || []).findIndex(i => i?.id === id);
    if (idx > -1) {
      return filterItem?.items?.[idx]?.value;
    }
    return '';
  };

  // Responsive values
  const contentWidth = getByScreenSize('90%', '80%');
  const containerMaxWidth = 960;
  const topContainerWidth = getByScreenSize('55%', '40%');
  const imageContainerWidth = getByScreenSize('70%', '40%');
  const buttonWidth = getByScreenSize('90%', '30%');
  const textFontSize = getByScreenSize(theme.text.s2, theme.text.s3);
  const resetFontSize = getByScreenSize(theme.text.s7, theme.text.s6);

  // Web-specific styles
  const filtersContainerStyle = {
    ...(isWeb &&
      !isMobile && {
        maxWidth: 800,
      }),
  };

  const _renderContent = () => {
    return (
      <View style={styles.contentContainer}>
        {/* Linear gradient as a background with fixed position */}
        {!isComponent && (
          <LinearGradient
            colors={[
              theme.tabNews.startLinearGradient,
              theme.tabNews.secondLinearGradient,
              theme.tabNews.thirdLinearGradient,
            ]}
            start={{x: 0, y: 0}}
            end={{x: 1, y: 1}}
            style={styles.gradientContainer}
          />
        )}

        <Container style={{paddingTop: headerHeight}}>
          <ContentWrapper maxWidth={containerMaxWidth}>
            {topComponent && topComponent()}
            <TopContainer width={topContainerWidth}>
              <ImageContainer width={imageContainerWidth}>
                <Image
                  resizeMode={'contain'}
                  hideLoader
                  defaultImage={
                    tab_id && tabImages[tab_id as keyof typeof tabImages]
                      ? tabImages[tab_id as keyof typeof tabImages]
                      : homePopularTopicsTabs[
                          (tab_id || 1) % homePopularTopicsTabs.length
                        ].img
                  }
                />
              </ImageContainer>
              <IconContainer>
                <Icon name={'recordBackground'} size={'100%'} />
              </IconContainer>
            </TopContainer>
            <TitleContainer width={contentWidth}>
              <Title bold fontSize={textFontSize}>
                {tr('app.appName')}
              </Title>
              <Title bold fontSize={textFontSize}>
                {tabName || ''}
              </Title>
            </TitleContainer>
            <FiltersContainer
              width={contentWidth}
              style={filtersContainerStyle}>
              {(tabFilters || []).length === 0 && <FiltersModalSkeleton />}
              {(tabFilters || []).map(filterItem => {
                let dates: Array<Dayjs> = [];
                if (filterItem.key === 'date') {
                  dates = (filterItem?.items || [])
                    .map(date =>
                      dayjs(date.value).isValid() ? dayjs(date.value) : null,
                    )
                    .filter((d): d is Dayjs => d !== null);
                }
                if (
                  (filterItem?.items || []).length <= 1 &&
                  filterItem.key !== 'date'
                ) {
                  return null;
                }

                return (
                  <TouchableOpacity
                    onPress={() => {
                      if (filterItem.key === 'date') {
                        openDateRangePickerModal({
                          onConfirm: val => {
                            if (val?.date) {
                              const date = dayjs(val?.date);
                              handleChange(
                                filterItem?.key || '',
                                date,
                                setFiltersResult,
                              );
                            }
                          },
                          datePickerProps: {
                            mode: 'single',
                            disabledDates: (date: any) =>
                              !(dates || []).some(enabledDate =>
                                enabledDate.isSame(date, 'day'),
                              ),
                            minDate: dayjs.min(dates),
                            maxDate: dayjs.max(dates),
                          },
                          currentRange: {
                            //@ts-ignore
                            date: getValue(filterItem)
                              ? dayjs(getValue(filterItem))
                              : undefined,
                          },
                        });
                        return;
                      }
                      toggleSettingsModal({
                        title:
                          tr('tabNews.select') +
                          ' ' +
                          (filterItem?.label || ''),
                        component: () => (
                          <FiltersSheet
                            filters={filterItem?.items || []}
                            selectedFilterId={
                              filtersResult?.[filterItem?.key || '']
                            }
                            onItemPress={item =>
                              handleChange(
                                filterItem?.key || '',
                                item?.id,
                                setFiltersResult,
                              )
                            }
                          />
                        ),
                      });
                    }}
                    key={filterItem?.key}
                    style={styles.webPointer}>
                    <TextInput
                      label={
                        tr('tabNews.select') + ' ' + (filterItem?.label || '')
                      }
                      labelStyle={{
                        color: theme.text.light,
                      }}
                      isLabelBold
                      pointerEvents={'none'}
                      editable={false}
                      value={
                        filterItem.key === 'date' && filtersResult.date
                          ? dayjs(filtersResult.date).format('YYYY-MM-DD')
                          : getValue(filterItem) ||
                            tr('tabNews.all') + ' ' + (filterItem?.label || '')
                      }
                      style={{
                        color: theme.text.light,
                      }}
                      containerStyle={{
                        marginBottom: theme.text.s55,
                      }}
                      inputContainerStyle={{
                        borderColor: theme.text.light,
                        backgroundColor: 'transparent',
                      }}
                      numberOfLines={1}
                    />
                  </TouchableOpacity>
                );
              })}
            </FiltersContainer>
            <TouchableContainer
              width={contentWidth}
              onPress={() => setFiltersResult({})}
              style={styles.webPointer}>
              <ResetFilter bold fontSize={resetFontSize}>
                {tr('tabNews.reset')}
              </ResetFilter>
            </TouchableContainer>
            <ButtonContainer>
              <GenericButton
                label={tr('tabNews.confirm')}
                disabled={
                  Object.values(filtersResult).filter(i => i).length === 0
                }
                onPress={() =>
                  navigation?.navigate(screens.content.tabNewsResults, {
                    ...tabNews,
                    filtersResult,
                  })
                }
                radius={100}
                style={{
                  width: buttonWidth,
                  alignSelf: 'center',
                  backgroundColor: theme.tabNews.button,
                  marginTop: hdp(2),
                  marginBottom: hdp(2),
                  ...(isWeb && !isMobile && styles.button),
                }}
              />
            </ButtonContainer>
            <TouchableContainer
              width={contentWidth}
              onPress={() =>
                navigation?.navigate(screens.content.tabNewsResults, tabNews)
              }
              style={styles.webPointer}>
              <ResetFilter isUnderline bold fontSize={resetFontSize}>
                {tr('tabNews.proceed')}
              </ResetFilter>
            </TouchableContainer>
            <Space bottomInset={bottomInset} extraSpace={hdp(15)} />
          </ContentWrapper>
        </Container>
      </View>
    );
  };

  if (isComponent) {
    return _renderContent();
  }

  return (
    <MainLayout
      enableScroll
      backHeader
      menuHeader
      noPadding
      title={tabName}
      noPaddingHeader>
      {_renderContent()}
    </MainLayout>
  );
};

export default FilterTabNews;
